import { Button, Col, Form, Row, Switch, TimePicker } from "antd";
import React from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

import { SelectOption } from "../../../libs/types";
import { InputField, SelectField } from "../../atoms/FormElement";
import { InputNumberField } from "../../atoms/FormElement/InputNumberField";

import { AmenityIconUpload } from "./AmenityIconUpload";
import { findOverlappingSlot, formatTimeValue, isValidTimeRange } from "./timeSlotUtils";
import {
  CatalogueId,
  FeedbackQuestionItem,
  FormType,
  MarketingConfigurationFormData,
  MarketingConfigurationItem,
  ViewingTimeSlotItem,
  isAmenityCatalogue,
  isIconUploadCatalogue,
} from "./types";

import type { Dayjs } from "dayjs";

export type MarketingConfigurationFormProps = {
  methods: UseFormReturn<MarketingConfigurationFormData>;
  formType: FormType;
  catalogueId: CatalogueId;
  organizationOptions: SelectOption[];
  existingTimeSlots?: ViewingTimeSlotItem[];
  existingFeedbackQuestions?: FeedbackQuestionItem[];
  existingItems?: MarketingConfigurationItem[];
  enableMarketingPublicList?: boolean;
  editingUid?: string | null;
  onSubmit: (data: MarketingConfigurationFormData) => void;
  onCancel: () => void;
};

export const MarketingConfigurationForm = (props: MarketingConfigurationFormProps) => {
  const {
    methods,
    formType,
    catalogueId,
    organizationOptions,
    existingTimeSlots = [],
    existingFeedbackQuestions = [],
    existingItems = [],
    enableMarketingPublicList = false,
    editingUid = null,
    onSubmit,
    onCancel,
  } = props;
  const { control, formState, handleSubmit, trigger, getValues } = methods;
  const { errors } = formState;

  const watchedOrganization = useWatch({ control, name: "organization" });

  const isAmenity = isAmenityCatalogue(catalogueId);
  const isViewingDay = catalogueId === "viewingDaysConfig";
  const isViewingTimeSlot = catalogueId === "viewingTimeSlotsConfig";
  const isFeedback = catalogueId === "feedbackQuestionnaireConfig";
  const isPublicListUrl = catalogueId === "marketingPublicListUrl";

  const validateTimeSlotRange = (value: Dayjs | null, field: "startTime" | "endTime") => {
    if (!value || !value.isValid()) {
      return "This field is required";
    }

    // Range/overlap messages belong on End Time only.
    if (field === "startTime") return true;

    const formValues = getValues();
    const startTime = formatTimeValue(formValues.startTime);
    const endTime = formatTimeValue(value);

    if (!startTime || !endTime) return true;
    if (!isValidTimeRange(startTime, endTime)) {
      return "End time must be after start time";
    }

    const overlap = findOverlappingSlot(startTime, endTime, existingTimeSlots, editingUid);
    if (overlap) {
      return `Overlaps with ${overlap.startTime} - ${overlap.endTime}`;
    }

    return true;
  };

  const commitTimeValue = (onChange: (value: Dayjs | null) => void, value: unknown) => {
    const next = (Array.isArray(value) ? (value[0] ?? null) : value) as Dayjs | null;
    onChange(next);
    void trigger(["startTime", "endTime"]);
  };

  const validateUniqueOrder = (value: number | string | null) => {
    if (value === null || value === undefined || value === "") return true;

    const order = Number(value);
    if (Number.isNaN(order)) return true;

    const orderExists = existingFeedbackQuestions.some((item) => {
      if (editingUid && item.uid === editingUid) return false;
      return Number(item.order) === order;
    });
    if (orderExists) {
      return "Order already exists";
    }

    return true;
  };

  const validateAtLeastOneActive = (value: boolean) => {
    if (catalogueId !== "viewingDaysConfig" || !enableMarketingPublicList || value) {
      return true;
    }

    const hasOtherActive = existingItems.some((item) => {
      if (editingUid && item.uid === editingUid) return false;
      return item.isActive;
    });

    if (!hasOtherActive) {
      return "At least one viewing day must be active when marketing public list is enabled";
    }

    return true;
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Row gutter={12}>
        <Col span={24}>
          <SelectField
            isRequired
            fieldname="organization"
            label="Organization"
            control={control}
            isDisabled
            selectOption={organizationOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Select Organization" }}
            validate={errors?.organization && "error"}
            validMessage={errors?.organization?.message}
          />
        </Col>

        {(isAmenity || isViewingDay || isFeedback) && (
          <Col span={24}>
            <InputField
              isRequired
              fieldname="label"
              label="Label"
              control={control}
              rules={{ required: "This field is required" }}
              iProps={{ placeholder: "Enter label", disabled: isViewingDay && formType === "edit" }}
              validate={errors?.label && "error"}
              validMessage={errors?.label?.message}
            />
          </Col>
        )}

        {isPublicListUrl && (
          <Col span={24}>
            <InputField
              isRequired
              fieldname="url"
              label="Public List URL"
              control={control}
              rules={{ required: "This field is required" }}
              iProps={{ placeholder: "https://example.com/listings" }}
              validate={errors?.url && "error"}
              validMessage={errors?.url?.message}
            />
          </Col>
        )}

        {isIconUploadCatalogue(catalogueId) && (
          <Col span={24}>
            <Form.Item label="Icon">
              <Controller
                name="url"
                control={control}
                render={({ field }) => (
                  <AmenityIconUpload
                    value={field.value}
                    onChange={field.onChange}
                    catalogueId={catalogueId}
                    organizationName={watchedOrganization?.label}
                  />
                )}
              />
            </Form.Item>
          </Col>
        )}

        {isViewingTimeSlot && (
          <>
            <Col span={12}>
              <Form.Item
                required
                label="Start Time"
                validateStatus={errors?.startTime && "error"}
                help={errors?.startTime?.message}
              >
                <Controller
                  name="startTime"
                  control={control}
                  rules={{
                    validate: (value) => validateTimeSlotRange(value, "startTime"),
                  }}
                  render={({ field }) => (
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      value={field.value}
                      popupClassName="marketing-config-time-picker--no-confirm"
                      showNow={false}
                      onSelect={(value) => {
                        commitTimeValue(field.onChange, value);
                      }}
                      onChange={(value) => {
                        commitTimeValue(field.onChange, value);
                      }}
                      placeholder="Select start time"
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                required
                label="End Time"
                validateStatus={errors?.endTime && "error"}
                help={errors?.endTime?.message}
              >
                <Controller
                  name="endTime"
                  control={control}
                  rules={{
                    validate: (value) => validateTimeSlotRange(value, "endTime"),
                  }}
                  render={({ field }) => (
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      value={field.value}
                      popupClassName="marketing-config-time-picker--no-confirm"
                      showNow={false}
                      onSelect={(value) => {
                        commitTimeValue(field.onChange, value);
                      }}
                      onChange={(value) => {
                        commitTimeValue(field.onChange, value);
                      }}
                      placeholder="Select end time"
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {isFeedback && (
          <Col span={24}>
            <InputNumberField
              isRequired
              fieldname="order"
              label="Order"
              control={control}
              rules={{
                required: "This field is required",
                validate: validateUniqueOrder,
              }}
              inputNumberProps={{ placeholder: "Order", min: 1 }}
            />
          </Col>
        )}

        {!isPublicListUrl && (
          <Col span={24}>
            <Form.Item
              label="Status"
              style={{ marginBottom: 24 }}
              validateStatus={errors?.isActive && "error"}
              help={errors?.isActive?.message}
            >
              <Controller
                name="isActive"
                control={control}
                rules={{ validate: validateAtLeastOneActive }}
                render={({ field }) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Switch
                      className="switch"
                      checked={field.value}
                      onChange={(checked) => {
                        field.onChange(checked);
                        void trigger("isActive");
                      }}
                    />
                    <span>{field.value ? "Active" : "Inactive"}</span>
                  </div>
                )}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={24} style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            {formType === "add" ? "Add" : "Save"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
